import React, {
  HTMLProps,
  MutableRefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import ResizeObserver from 'resize-observer-polyfill';
import styled from 'styled-components';
import { TraceBoardEmptyState } from './interfaces/TraceBoardEmptyState';
import { TraceBoardCanEditItemFunction } from './interfaces/TraceBoardCanEditItemFunction';
import { TraceBoardCanLinkNewItemFunction } from './interfaces/TraceBoardCanLinkNewItemFunction';
import { TraceBoardColumn } from './interfaces/TraceBoardColumn';
import { TraceBoardFilterColumnItemsFunction } from './interfaces/TraceBoardFilterColumnItemsFunction';
import { TraceBoardFilterOrphanedItemsFunction } from './interfaces/TraceBoardFilterOrphanedItemsFunction';
import { TraceBoardOnAddItemFunction } from './interfaces/TraceBoardOnAddItem';
import { TraceBoardOnUpdateItemFunction } from './interfaces/TraceBoardOnUpdateItem';
import {
  TraceBoardContext,
  TraceBoardContextState,
  TraceBoardProvider
} from './TraceBoardContext';
import TraceBoardHeader from './TraceBoardHeader';
import TraceBoardRow from './TraceBoardRow';
import { useTimeout } from '../timeout.hook';

const StyledDiv = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  // handle overflow when browser is smaller then total min column width
  &.overflow {
    overflow: auto;
    .board-header {
      width: fit-content;
      .column-header {
        width: 320px;
      }
    }
    .board-row {
      width: fit-content;
      .board-column {
        width: 320px;
      }
    }
  }
  .border-body {
    flex: 1;
  }
  .row-heading {
    display: block;
    margin: 16px 12px -12px 12px;
  }
`;

export type TraceBoardProps = React.ComponentProps<typeof StyledDiv> &
  HTMLProps<HTMLDivElement> & {
    innerRef?: MutableRefObject<TraceBoardContextState>;
    heading?: string | JSX.Element | null;
    orphanedRowHeading?: string | JSX.Element | null;
    columns: TraceBoardColumn[];
    loadingColumn?: number | null;
    canEditItem?: boolean | TraceBoardCanEditItemFunction;
    canLinkNewItem?: boolean | TraceBoardCanLinkNewItemFunction;
    isLoading: boolean;
    filterColumnItems: TraceBoardFilterColumnItemsFunction;
    filterOrphanedItems?: TraceBoardFilterOrphanedItemsFunction;
    onAddItem?: TraceBoardOnAddItemFunction;
    onUpdateItem?: TraceBoardOnUpdateItemFunction;
  };

export const TraceBoard: React.FC<TraceBoardProps> = ({
  innerRef,
  heading,
  orphanedRowHeading,
  columns,
  className,
  canEditItem,
  canLinkNewItem,
  isLoading,
  filterColumnItems,
  filterOrphanedItems,
  onAddItem,
  onUpdateItem,
  ...props
}) => {
  const [overflow, setOverflow] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null!);
  const [delay] = useTimeout();
  const [viewPosition, setViewPosition] = useState([0, 0]);
  const observerRef = useRef(
    new ResizeObserver(() => {
      // check if overflow scrolling needed
      const element = elementRef.current;
      const context = innerRef?.current;
      const columnMinWidth = 320;
      setOverflow(
        Boolean(
          element &&
            context &&
            element.clientWidth < context.columns.length * columnMinWidth
        )
      );
    })
  );

  const updateViewPosition = () => {
    setViewPosition([
      elementRef.current.scrollLeft,
      elementRef.current.scrollTop
    ]);
  };

  useEffect(() => {
    const observer = observerRef.current;
    const element = elementRef.current;
    observer.observe(element);
    return () => {
      observer.unobserve(element);
    };
  }, []);

  useLayoutEffect(() => {
    if (!isLoading) {
      return delay(() => {
        elementRef.current.scrollTo({
          behavior: 'smooth',
          left: viewPosition[0],
          top: viewPosition[1]
        });
      }, 200);
    }
  }, [delay, isLoading, viewPosition]);

  const classes = ['trace-board'];
  if (className) classes.push(className);
  if (overflow) classes.push('overflow');

  return (
    <TraceBoardProvider
      innerRef={innerRef}
      columns={columns}
      canEditItem={canEditItem}
      canLinkNewItem={canLinkNewItem}
      filterColumnItems={filterColumnItems}
      filterOrphanedItems={filterOrphanedItems}
      onAddItem={(...args) => {
        updateViewPosition();
        onAddItem?.(...args);
      }}
      onUpdateItem={(...args) => {
        updateViewPosition();
        onUpdateItem?.(...args);
      }}
    >
      <TraceBoardContext.Consumer>
        {({ columns }) => {
          const sourceColumn = columns[0];
          const isEmpty =
            !isLoading &&
            columns.length > 0 &&
            columns.every(columns => columns.items.length === 0);
          return (
            <StyledDiv
              ref={elementRef}
              className={classes.join(' ')}
              {...props}
            >
              <TraceBoardHeader />
              {!isLoading && (
                <div className='board-body'>
                  {sourceColumn?.items.map((item, index) => (
                    <TraceBoardRow
                      key={item.id}
                      rowIndex={index}
                      primaryItem={item}
                      heading={index === 0 ? heading : undefined}
                    />
                  ))}
                  {filterOrphanedItems && (
                    <>
                      <TraceBoardRow
                        rowIndex={sourceColumn?.items.length}
                        heading={orphanedRowHeading || 'Orphaned Cards'}
                      />
                    </>
                  )}
                </div>
              )}
              {isLoading && (
                <Box
                  mx={2}
                  my={3}
                  display='flex'
                  alignItems='center'
                  flexDirection='column'
                >
                  <CircularProgress color='primary' />
                  <Box mt={2}>
                    <Typography variant='body1' className='text'>
                      Loading Board...
                    </Typography>
                  </Box>
                </Box>
              )}
              {isEmpty && <TraceBoardEmptyState />}
            </StyledDiv>
          );
        }}
      </TraceBoardContext.Consumer>
    </TraceBoardProvider>
  );
};

export default TraceBoard;
